import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { DollarSign, TrendingUp, MousePointer, ShoppingCart, ArrowUpRight } from 'lucide-react';
import { commissionTiers } from '../mock';
import { authAPI, userAPI } from '../services/apiService';
import { toast } from 'sonner';

const Dashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const isAuthenticated = localStorage.getItem('isAuthenticated');
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    loadUserData();
  }, [navigate]);

  const loadUserData = async () => {
    try {
      const [userData, userStats] = await Promise.all([
        authAPI.getCurrentUser(),
        userAPI.getStats()
      ]);
      
      setUser(userData);
      setStats(userStats);
    } catch (error) {
      toast.error('Failed to load user data');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading || !user || !stats) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  const currentTier = commissionTiers.find(t => t.tier === stats.currentTier);

  return (
    <div className="min-h-screen py-12">
      <div className="max-w-7xl mx-auto px-6">
        {/* Welcome Header */}
        <div className="mb-8 bg-white/80 backdrop-blur-sm p-6 rounded-2xl border-4 border-red-300 shadow-xl">
          <h1 className="text-4xl font-bold mb-2" style={{
            background: 'linear-gradient(135deg, #dc2626 0%, #f59e0b 50%, #ec4899 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>Welcome back, {user.firstName}!</h1>
          <p className="text-gray-700 text-lg font-medium">Here's your referral performance overview</p>
        </div>

        {/* Stats Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="border-3 border-red-300 hover:border-red-500 transition-all duration-300 shadow-lg bg-white/90 backdrop-blur-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                  <DollarSign className="h-6 w-6 text-teal-600" />
                </div>
                <ArrowUpRight className="h-5 w-5 text-green-500" />
              </div>
              <p className="text-sm text-gray-600 mb-1">Available Balance</p>
              <p className="text-3xl font-bold text-gray-900">₹{stats.balance.toLocaleString()}</p>
            </CardContent>
          </Card>

          <Card className="border-3 border-yellow-300 hover:border-yellow-500 transition-all duration-300 shadow-lg bg-white/90 backdrop-blur-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <TrendingUp className="h-6 w-6 text-blue-600" />
                </div>
                <ArrowUpRight className="h-5 w-5 text-orange-500" />
              </div>
              <p className="text-sm text-gray-600 mb-1">Pending</p>
              <p className="text-3xl font-bold text-gray-900">₹{stats.pendingBalance.toLocaleString()}</p>
              <p className="text-xs text-gray-500 mt-1">Awaiting confirmation</p>
            </CardContent>
          </Card>

          <Card className="border-3 border-pink-300 hover:border-pink-500 transition-all duration-300 shadow-lg bg-white/90 backdrop-blur-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <MousePointer className="h-6 w-6 text-green-600" />
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-1">Total Clicks</p>
              <p className="text-3xl font-bold text-gray-900">{stats.totalClicks.toLocaleString()}</p>
            </CardContent>
          </Card>

          <Card className="border-3 border-red-300 hover:border-red-500 transition-all duration-300 shadow-lg bg-white/90 backdrop-blur-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <ShoppingCart className="h-6 w-6 text-purple-600" />
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-1">Conversions</p>
              <p className="text-3xl font-bold text-gray-900">{stats.totalConversions}</p>
            </CardContent>
          </Card>
        </div>

        {/* Lifetime Earnings & Tier */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <Card className="border-3 border-yellow-400 shadow-lg bg-white/90 backdrop-blur-sm">
            <CardHeader>
              <CardTitle>Total Earned (Lifetime)</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-bold text-red-600">₹{stats.lifetimeEarnings.toLocaleString()}</p>
            </CardContent>
          </Card>

          <Card className="border-4 shadow-lg bg-white/90 backdrop-blur-sm" style={{ borderColor: currentTier?.color }}>
            <CardHeader>
              <CardTitle>Current Tier</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold" style={{ color: currentTier?.color }}>{currentTier?.name}</p>
                  <p className="text-gray-600 mt-1">{currentTier?.salesRange}</p>
                  <p className="text-sm text-gray-500 mt-2">This month: {stats.salesThisMonth} sales</p>
                </div>
                <div className="text-right">
                  <p className="text-4xl font-bold" style={{ color: currentTier?.color }}>{currentTier?.rate}%</p>
                  <p className="text-sm text-gray-600">Commission</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Button 
            onClick={() => navigate('/place-order')}
            className="h-auto py-6 text-white border-2 border-green-400 shadow-lg"
            style={{ background: 'linear-gradient(135deg, #10b981 0%, #14b8a6 100%)' }}
          >
            <div className="text-left w-full">
              <p className="font-semibold text-lg">Place Order</p>
              <p className="text-sm opacity-90">Submit customer order</p>
            </div>
          </Button>

          <Button 
            onClick={() => navigate('/referral-links')}
            className="h-auto py-6 text-white border-2 border-yellow-400 shadow-lg"
            style={{ background: 'linear-gradient(135deg, #dc2626 0%, #f59e0b 100%)' }}
          >
            <div className="text-left w-full">
              <p className="font-semibold text-lg">Referral Links</p>
              <p className="text-sm opacity-90">Get your unique link</p>
            </div>
          </Button>

          <Button 
            onClick={() => navigate('/transactions')}
            className="h-auto py-6 text-white border-2 border-pink-400 shadow-lg"
            style={{ background: 'linear-gradient(135deg, #ec4899 0%, #f59e0b 100%)' }}
          >
            <div className="text-left w-full">
              <p className="font-semibold text-lg">Transactions</p>
              <p className="text-sm opacity-90">View sales history</p>
            </div>
          </Button>

          <Button 
            onClick={() => navigate('/messages')}
            className="h-auto py-6 text-white border-2 border-blue-400 shadow-lg"
            style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)' }}
          >
            <div className="text-left w-full">
              <p className="font-semibold text-lg">Messages</p>
              <p className="text-sm opacity-90">Contact support</p>
            </div>
          </Button>
        </div>

        {/* Payout Button */}
        <div className="mt-6">
          <Button 
            className="w-full h-14 text-white border-2 border-green-400 shadow-lg"
            style={{ background: 'linear-gradient(135deg, #10b981 0%, #14b8a6 100%)' }}
            disabled={stats.balance < 1000}
          >
            <div className="text-left w-full">
              <p className="font-semibold text-lg">Request Payout</p>
              <p className="text-sm opacity-90">
                {stats.balance >= 1000 ? 'Withdraw funds' : `Min ₹1,000 required`}
              </p>
            </div>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
